import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useBrand } from '../hooks/useBrand';
import { useBrandCreate } from '../hooks/useBrandCreate';
import { 
  ArrowLeft, Save, Upload, Globe, Hash, MessageSquare, 
  Megaphone, Palette, Lightbulb, Target, Users, TrendingUp,
  FileText, X, Plus, Loader2, Edit3, Trash2, Sparkles, RefreshCw
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TagInput } from '../components/common/TagInput';
import { LanguageInput } from '../components/common/LanguageInput';
import { IndustrySelect } from '../components/common/IndustrySelect';
import { ContentToneSelect } from '../components/common/ContentToneSelect';
import { MarketScopeSelect } from '../components/common/MarketScopeSelect';
import { BusinessModelSelect } from '../components/common/BusinessModelSelect';
import { DistributionTypeSelect } from '../components/common/DistributionTypeSelect';
import { ImageStyleSelect } from '../components/common/ImageStyleSelect';
import { SocialFrequencyDisplay } from '../components/common/SocialFrequencyDisplay';
import { AddSocialNetworkModal } from '../components/common/AddSocialNetworkModal';
import { TabGroup } from '../components/common/TabGroup';
import { AddItemInput } from '../components/common/AddItemInput';
import { ColorEditModal } from '../components/common/ColorEditModal';
import { FontEditModal } from '../components/common/FontEditModal';
import { loadBrandFonts } from '../utils/brandFonts';
import { BrandGenerationLoader } from '../components/brands/BrandGenerationLoader';
import { useBrandMutation } from '../hooks/useBrandMutation';
import type { PostingFrequency } from '../components/common/SocialFrequencyDisplay';
import { logger } from '../utils/logger';
import { mapAnalysisToBrand } from '../utils/mapAnalysisToBrand';
import { auth } from '../config/firebase';
import { BrandFormErrorBoundary } from '../components/common/BrandFormErrorBoundary';

interface SocialMedia {
  platform: string;
  frequency: PostingFrequency;
}

interface Summary {
  recap: string;
  takeaways: string[];
}

interface ColorPalette {
  primary: string[];
  secondary: string[];
  accent: string[];
  neutral: string[];
  recommended?: {
    complementary: string[];
    improvements: string;
  };
}

interface Typography {
  headings: {
    h1: string;
    h2: string;
    h3: string;
  };
  body: {
    primary: string;
    secondary: string;
  };
  specialty: {
    buttons: string;
    captions: string;
    navigation: string;
  };
  fallbacks: string[];
  recommendations?: {
    improvements: string;
    hierarchy: string;
  };
}

interface VisualContentStrategy {
  colorUsage: {
    socialMediaPosts: string;
    callToActions: string;
    backgrounds: string;
    seasonalAdaptations: string;
  };
  typographyGuidelines: {
    socialMediaHeaders: string;
    bodyText: string;
    quotesTestimonials: string;
    hashtagsKeywords: string;
  };
  visualElementsToEmphasize: string[];
  designConsistencyTips: string[];
  brandExtensions: {
    socialTemplates: string;
    logoUsage: string;
    imageFilters: string;
  };
}

interface Brand {
  id: string;
  name: string;
  description: string;
  logo: string;
  website: string;
  industryId: string;
  values: string[];
  keywords: string[];
  languages: string[];
  contentTone: string;
  imageStyle: string;
  voice: string;
  uniqueSellingPoints: string[];
  recommendedFormats: string[];
  socialMedias: SocialMedia[];
  themes: string[];
  contentGaps: string[];
  engagementTactics: string[];
  growthOpportunities: string[];
  summary: Summary;
  visualIdentity: {
    colorPalette: ColorPalette;
    typography: Typography;
  };
  visualContentStrategy: VisualContentStrategy;
  goToMarketStrategy: {
    short: string;
    targetSegments: string[];
    keyPersonaTypes: string[];
    marketScope: string;
    priorityMarkets: string[];
    businessModel: string;
    distributionType: string;
  };
}

const emptyBrand: Brand = {
  id: '',
  name: '',
  description: '',
  logo: '',
  website: '',
  industryId: 'advertising-marketing',
  values: [],
  keywords: [],
  languages: ['en'],
  contentTone: 'professional',
  imageStyle: 'realistic',
  voice: '',
  uniqueSellingPoints: [],
  recommendedFormats: [],
  socialMedias: [] as SocialMedia[],
  themes: [],
  contentGaps: [],
  engagementTactics: [],
  growthOpportunities: [],
  summary: {
    recap: '',
    takeaways: []
  } as Summary,
  visualIdentity: {
    colorPalette: {
      primary: [],
      secondary: [],
      accent: [],
      neutral: [],
      recommended: {
        complementary: [],
        improvements: ''
      }
    } as ColorPalette,
    typography: {
      headings: {
        h1: '',
        h2: '',
        h3: ''
      },
      body: {
        primary: '',
        secondary: ''
      },
      specialty: {
        buttons: '',
        captions: '',
        navigation: ''
      },
      fallbacks: [],
      recommendations: {
        improvements: '',
        hierarchy: ''
      }
    } as Typography
  },
  visualContentStrategy: {
    colorUsage: {
      socialMediaPosts: '',
      callToActions: '',
      backgrounds: '',
      seasonalAdaptations: ''
    },
    typographyGuidelines: {
      socialMediaHeaders: '',
      bodyText: '',
      quotesTestimonials: '',
      hashtagsKeywords: ''
    },
    visualElementsToEmphasize: [],
    designConsistencyTips: [],
    brandExtensions: {
      socialTemplates: '',
      logoUsage: '',
      imageFilters: ''
    }
  } as VisualContentStrategy,
  goToMarketStrategy: {
    short: '',
    targetSegments: [],
    keyPersonaTypes: [],
    marketScope: 'local',
    priorityMarkets: [],
    businessModel: 'B2C',
    distributionType: 'digital'
  }
};

// Helper function to get Google Fonts info
const getGoogleFontInfo = (fontName: string): { styles: number; isGoogleFont: boolean } => {
  const googleFonts: { [key: string]: number } = {
    'Inter': 18,
    'Roboto': 12,
    'Open Sans': 10,
    'Lato': 9,
    'Montserrat': 18,
    'Source Sans Pro': 12,
    'Oswald': 6,
    'Raleway': 18,
    'Poppins': 18,
    'Nunito': 12,
    'Playfair Display': 9,
    'Montagu Slab': 7,
    'Merriweather': 8,
    'Crimson Text': 6,
    'PT Sans': 4,
    'Ubuntu': 8,
    'Libre Baskerville': 3,
    'Work Sans': 9,
    'Fira Sans': 10,
    'Noto Sans': 12,
    'Bricolage Grotesque': 4,
    'Albert Sans': 18,
    'DM Sans': 11,
    'Plus Jakarta Sans': 14,
    'Outfit': 9,
    'Manrope': 8,
    'Space Grotesk': 7,
    'Lexend': 9,
    'Satoshi': 10,
    'Figtree': 9
  };
  
  const styles = googleFonts[fontName] || 1;
  const isGoogleFont = !!googleFonts[fontName];
  
  return { styles, isGoogleFont };
};

// Helper function to generate color names
const getColorName = (hexColor: string, category: string): string => {
     // Exact color matches
  const exactColors: { [key: string]: string } = {
    '#FFFFFF': 'Pure White',
    '#000000': 'Pure Black',
    '#FF0000': 'Pure Red',
    '#00FF00': 'Pure Green', 
    '#0000FF': 'Pure Blue',
    '#FFFF00': 'Pure Yellow',
    '#FF00FF': 'Pure Magenta',
    '#00FFFF': 'Pure Cyan',
    '#808080': 'Gray',
    '#6428E3': 'Bright Purple',
    '#5622C6': 'Purple',
    '#CFBFF7': 'Light Purple',
    '#333333': 'Dark Gray',
    '#333': 'Dark Gray',
    '#666666': 'Gray',
    '#666': 'Gray',
    '#999999': 'Light Gray',
    '#999': 'Light Gray',
    '#CCCCCC': 'Light Gray',
    '#CCC': 'Light Gray'
  };

  const exact = exactColors[hexColor.toUpperCase()];
  if (exact) return exact;

  // Convert hex to RGB
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate luminance for brightness
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
     // Determine if it's grayscale (much stricter threshold)
  const isGrayscale = Math.abs(r - g) < 8 && Math.abs(g - b) < 8 && Math.abs(r - b) < 8;
  
  if (isGrayscale) {
    if (luminance > 0.9) return 'Pure White';
    if (luminance > 0.7) return 'Light Gray';
    if (luminance > 0.5) return 'Gray';
    if (luminance > 0.3) return 'Dark Gray';
    if (luminance > 0.1) return 'Charcoal';
    return 'Pure Black';
  }

  // Find dominant color channel
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const saturation = max === 0 ? 0 : (max - min) / max;
  
  // Low saturation = grayish
  if (saturation < 0.2) {
    if (luminance > 0.7) return 'Light Gray';
    if (luminance > 0.3) return 'Gray';
    return 'Dark Gray';
  }

  // Determine hue
  let hue = 0;
  if (max === r) {
    hue = ((g - b) / (max - min)) * 60;
  } else if (max === g) {
    hue = (2 + (b - r) / (max - min)) * 60;
  } else {
    hue = (4 + (r - g) / (max - min)) * 60;
  }
  
  if (hue < 0) hue += 360;

  // Get brightness prefix
  const brightness = luminance > 0.7 ? 'Bright ' : luminance < 0.3 ? 'Dark ' : '';

  // Determine color name by hue
  if (hue >= 345 || hue < 15) return brightness + 'Red';
  if (hue < 45) return brightness + 'Orange';
  if (hue < 75) return brightness + 'Yellow';
  if (hue < 105) return brightness + 'Yellow Green';
  if (hue < 135) return brightness + 'Green';
  if (hue < 165) return brightness + 'Teal';
  if (hue < 195) return brightness + 'Cyan';
  if (hue < 225) return brightness + 'Blue';
  if (hue < 255) return brightness + 'Blue';
  if (hue < 285) return brightness + 'Purple';
  if (hue < 315) return brightness + 'Magenta';
  return brightness + 'Pink';
};

const BrandForm = () => {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState('branding');
  const [isAddNetworkModalOpen, setIsAddNetworkModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isColorModalOpen, setIsColorModalOpen] = useState(false);
  const [editingColor, setEditingColor] = useState<{
    color: string;
    type: 'primary' | 'secondary' | 'accent' | 'neutral';
    index: number;
  } | null>(null);
  
  const [fontModal, setFontModal] = useState(false);
  const [editingFont, setEditingFont] = useState<{
    font: string;
    type: 'heading' | 'body' | 'buttons' | 'captions' | 'navigation';
    subtype?: 'h1' | 'h2' | 'h3' | 'primary' | 'secondary';
  } | null>(null);
  
  // Refresh data state
  const [isRefreshModalOpen, setIsRefreshModalOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isRefreshSuccessModalOpen, setIsRefreshSuccessModalOpen] = useState(false);
  
  // Fill with AI state
  const [isFillWithAIModalOpen, setIsFillWithAIModalOpen] = useState(false);
  const [isUrlInputModalOpen, setIsUrlInputModalOpen] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [hasBeenFilledWithAI, setHasBeenFilledWithAI] = useState(false);
  
    const [brand, setBrand] = useState<Brand>(() => {
    // Check for prefilled data from website analysis
    const prefilledData = location.state?.prefilledData;
          logger.debug('BrandForm', 'Prefilled data loaded');

    if (prefilledData) {
      // Data should already be in the correct flat format after mapping
      return { ...emptyBrand, ...prefilledData } as Brand;
    }
    return emptyBrand as Brand;
  });
  const [originalBrand, setOriginalBrand] = useState<Brand>(emptyBrand as Brand);
  const isEditMode = Boolean(brandId);
  const brandMutation = useBrandMutation();
  const brandCreateMutation = useBrandCreate();

  // Fetch brand data if in edit mode
  const { data: brandData, isLoading: isBrandLoading, error: brandError } = useBrand(brandId || '');

  const themeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // The typography tab renders the brand's own families, so pull the catalogue
    // in here rather than at app boot.
    loadBrandFonts();
  }, []);

  useEffect(() => {
    if (!isEditMode) {
      setIsLoading(false);
      return;
    }

    if (brandData) {
      logger.debug('BrandForm', 'Brand data received from API');
      
      // Extract contentStrategy properties with fallbacks
      let socialMediasArray = [];
      
      // Get social media platforms from brandData.socialMedias or from contentStrategy.recommendedPlatforms
      if (brandData.socialMedias && brandData.socialMedias.length > 0) {
        socialMediasArray = [...brandData.socialMedias];
      } else if (brandData.contentStrategy?.recommendedPlatforms && brandData.contentStrategy.recommendedPlatforms.length > 0) {
        // Convert from recommendedPlatforms format to socialMedias format
        socialMediasArray = brandData.contentStrategy.recommendedPlatforms.map((platform: any) => ({
          platform: platform.platform || '',
          frequency: platform.postingFrequency || 'weekly'
        }));
      }
      
      // Safely initialize goToMarketStrategy from different possible API response fields
      const goToMarketStrategy = {
        short: brandData.goToMarketStrategy?.short || '',
        targetSegments: brandData.goToMarketStrategy?.targetSegments || brandData.targetSegments || [],
        keyPersonaTypes: brandData.goToMarketStrategy?.keyPersonaTypes || brandData.keyPersonaTypes || [],
        marketScope: brandData.goToMarketStrategy?.marketScope || brandData.marketScope || 'local',
        priorityMarkets: brandData.goToMarketStrategy?.priorityMarkets || brandData.priorityMarkets || [],
        businessModel: brandData.goToMarketStrategy?.businessModel || brandData.businessModel || 'B2C',
        distributionType: brandData.goToMarketStrategy?.distributionType || brandData.distributionType || 'digital'
      };
      
      // Safely initialize visualIdentity with proper fallbacks
      const visualIdentity = {
        colorPalette: {
          primary: brandData.visualIdentity?.colorPalette?.primary || [],
          secondary: brandData.visualIdentity?.colorPalette?.secondary || [],
          accent: brandData.visualIdentity?.colorPalette?.accent || [],
          neutral: brandData.visualIdentity?.colorPalette?.neutral || [],
          recommended: brandData.visualIdentity?.colorPalette?.recommended || {
            complementary: [],
            improvements: ''
          }
        },
        typography: {
          headings: {
            h1: brandData.visualIdentity?.typography?.headings?.h1 || '',
            h2: brandData.visualIdentity?.typography?.headings?.h2 || '',
            h3: brandData.visualIdentity?.typography?.headings?.h3 || ''
          },
          body: {
            primary: brandData.visualIdentity?.typography?.body?.primary || '',
            secondary: brandData.visualIdentity?.typography?.body?.secondary || ''
          },
          specialty: {
            buttons: brandData.visualIdentity?.typography?.specialty?.buttons || '',
            captions: brandData.visualIdentity?.typography?.specialty?.captions || '',
            navigation: brandData.visualIdentity?.typography?.specialty?.navigation || ''
          },
          fallbacks: brandData.visualIdentity?.typography?.fallbacks || [],
          recommendations: brandData.visualIdentity?.typography?.recommendations || {
            improvements: '',
            hierarchy: ''
          }
        }
      };
      
      // Safely initialize visualContentStrategy
      const visualContentStrategy = {
        colorUsage: {
          socialMediaPosts: brandData.visualContentStrategy?.colorUsage?.socialMediaPosts || '',
          callToActions: brandData.visualContentStrategy?.colorUsage?.callToActions || '',
          backgrounds: brandData.visualContentStrategy?.colorUsage?.backgrounds || '',
          seasonalAdaptations: brandData.visualContentStrategy?.colorUsage?.seasonalAdaptations || ''
        },
        typographyGuidelines: {
          socialMediaHeaders: brandData.visualContentStrategy?.typographyGuidelines?.socialMediaHeaders || '',
          bodyText: brandData.visualContentStrategy?.typographyGuidelines?.bodyText || '',
          quotesTestimonials: brandData.visualContentStrategy?.typographyGuidelines?.quotesTestimonials || '',
          hashtagsKeywords: brandData.visualContentStrategy?.typographyGuidelines?.hashtagsKeywords || ''
        },
        visualElementsToEmphasize: brandData.visualContentStrategy?.visualElementsToEmphasize || brandData.visualElementsToEmphasize || [],
        designConsistencyTips: brandData.visualContentStrategy?.designConsistencyTips || brandData.designConsistencyTips || [],
        brandExtensions: {
          socialTemplates: brandData.visualContentStrategy?.brandExtensions?.socialTemplates || '',
          logoUsage: brandData.visualContentStrategy?.brandExtensions?.logoUsage || '',
          imageFilters: brandData.visualContentStrategy?.brandExtensions?.imageFilters || ''
        }
      };
      
      const formattedBrandData = {
        ...brandData,
        // Ensure all array properties have fallback values
        socialMedias: socialMediasArray,
        values: brandData.values || [],
        keywords: brandData.keywords || [], 
        languages: brandData.languages || ['en'],
        imageStyle: brandData.imageStyle || 'realistic',
        uniqueSellingPoints: brandData.uniqueSellingPoints || [],
        recommendedFormats: brandData.contentStrategy?.recommendedFormats || brandData.recommendedFormats || [],
        
        // Extract from contentStrategy if available
        themes: brandData.contentStrategy?.themes || brandData.themes || [],
        contentGaps: brandData.contentStrategy?.contentGaps || brandData.contentGaps || [],
        engagementTactics: brandData.contentStrategy?.engagementTactics || brandData.engagementTactics || [],
        growthOpportunities: brandData.contentStrategy?.growthOpportunities || brandData.growthOpportunities || [],
        
        // Extract from overview if available
        summary: {
          recap: brandData.overview?.summary || (brandData.summary?.recap || ''),
          takeaways: brandData.overview?.takeaways || (brandData.summary?.takeaways || [])
        },
        
        // Use safely initialized objects
        goToMarketStrategy,
        visualIdentity,
        visualContentStrategy
      };
      
      logger.debug('BrandForm', 'Brand data formatted for display');
      setBrand(formattedBrandData);
      setOriginalBrand(formattedBrandData);
      setIsLoading(false);
    }
  }, [brandData, isEditMode]);

  const handleSave = async () => {
    try {
      // Validation des champs obligatoires - seulement le nom est vraiment requis
      const requiredFields = [];
      
      if (!brand.name?.trim()) {
        requiredFields.push(t('brands.edit.brandIdentity.name', 'Brand Name'));
      }
      
      // Note: Les autres champs ne sont plus obligatoires pour permettre la sauvegarde de marques incomplètes
      // comme SNCH qui peuvent avoir des données partielles
      
      if (requiredFields.length > 0) {
        alert(t('brands.validation.requiredFields', 'Les champs suivants sont obligatoires : ') + requiredFields.join(', '));
        return;
      }

      logger.info('BrandForm', `Saving brand: ${brand.name}`);
      setIsSaving(true);

      // Convert socialMedias to recommendedPlatforms format
      const recommendedPlatforms = brand.socialMedias.map((socialMedia: { platform: string; frequency: string }) => ({
        platform: socialMedia.platform,
        postingFrequency: socialMedia.frequency
      }));
      
      // Prepare contentStrategy object
      const contentStrategyObj = {
        themes: brand.themes || [],
        contentGaps: brand.contentGaps || [],
        engagementTactics: brand.engagementTactics || [],
        growthOpportunities: brand.growthOpportunities || [],
        recommendedFormats: brand.recommendedFormats || [],
        recommendedPlatforms: recommendedPlatforms
      };
      
      // Prepare overview object
      const overviewObj = {
        summary: brand.summary?.recap || '',
        takeaways: brand.summary?.takeaways || []
      };
      
      if (isEditMode) {
        // Get only changed fields
        const changedFields: Record<string, any> = {};
        
        // Compare current state with original state
        Object.entries(brand).forEach(([key, value]) => {
          const originalValue = (originalBrand as any)[key];
          const currentValueStr = JSON.stringify(value);
          const originalValueStr = JSON.stringify(originalValue);
          
          if (currentValueStr !== originalValueStr) {
            changedFields[key] = value;
            logger.debug('BrandForm', `Field '${key}' changed`, {
              original: originalValue,
              current: value
            });
          }
        });
        
        logger.info('BrandForm', `Detected ${Object.keys(changedFields).length} changed fields: ${Object.keys(changedFields).join(', ')}`);
        
        // If no changes and hasn't been filled with AI, don't make API call
        if (Object.keys(changedFields).length === 0 && !hasBeenFilledWithAI) {
          logger.warn('BrandForm', 'No changes detected, skipping save');
          navigate('/brand-strategy-lab');
          return;
        }
        
        // If filled with AI but no changes detected, force update with current brand data
        if (Object.keys(changedFields).length === 0 && hasBeenFilledWithAI) {
          logger.info('BrandForm', 'No changes detected but brand was filled with AI - forcing update');
          // Use all current brand data as changed fields (excluding id)
          Object.entries(brand).forEach(([key, value]) => {
            if (key !== 'id') {
              changedFields[key] = value;
            }
          });
        }
        
                  logger.debug('BrandForm', `Updating ${Object.keys(changedFields).length} changed fields`);
        
        try {
          // Update brand
          const result = await brandMutation.mutateAsync({
            brandId: brandId!,
            data: changedFields
          });
                      logger.info('BrandForm', `Brand updated successfully: ${brand.name}`);
          
          // Reset the AI fill flag after successful save
          setHasBeenFilledWithAI(false);
          navigate('/brand-strategy-lab');
        } catch (updateError: any) {
          logger.error('Failed to update brand:', JSON.stringify(updateError));
          
          // Try with minimal fields if the initial update fails
          if (changedFields.name || changedFields.description) {
            logger.info('Attempting minimal update with only name and description', 'BRAND_UPDATE');
            const minimalUpdate: Record<string, any> = {};
            if (changedFields.name) minimalUpdate.name = changedFields.name;
            if (changedFields.description) minimalUpdate.description = changedFields.description;
            
            try {
              const minimalResult = await brandMutation.mutateAsync({
                brandId: brandId!,
                data: minimalUpdate
              });
              logger.info('BrandForm', `Minimal brand update successful: ${brand.name}`);
              
              // Reset the AI fill flag after successful save
              setHasBeenFilledWithAI(false);
              navigate('/brand-strategy-lab');
              return;
            } catch (minimalError) {
              logger.error('Even minimal update failed:', JSON.stringify(minimalError));
              throw new Error(t('brands.edit.updateErrorMinimal', 'Could not update brand even with minimal fields'));
            }
          } else {
            throw updateError;
          }
        }
      } else {
        // Create new brand - Start with minimal data
        logger.info('Creating new brand with minimal data first', 'BRAND_CREATE');
        try {
          // First try with fields required by payload type
          const minimalData = {
            name: brand.name,
            description: brand.description || '',
            logo: brand.logo || '',
            website: brand.website || '',
            industryId: brand.industryId || 'advertising-marketing',
            languages: brand.languages || ['en'],
            // Add missing fields required by CreateBrandPayload with defaults
            values: brand.values || [],
            keywords: brand.keywords || [],
            contentTone: brand.contentTone || 'professional',
            imageStyle: brand.imageStyle || 'realistic',
            voice: brand.voice || '',
            uniqueSellingPoints: brand.uniqueSellingPoints || [],
            recommendedFormats: brand.recommendedFormats || [],
            socialMedias: brand.socialMedias || [],
            themes: brand.themes || [],
            contentGaps: brand.contentGaps || [],
            engagementTactics: brand.engagementTactics || [],
            growthOpportunities: brand.growthOpportunities || [],
            summary: brand.summary || { recap: '', takeaways: [] },
            // Include visual identity data (colors and fonts)
            visualIdentity: brand.visualIdentity || {
              colorPalette: {
                primary: [],
                secondary: [],
                accent: [],
                neutral: []
              },
              typography: {
                headings: { h1: '', h2: '', h3: '' },
                body: { primary: '', secondary: '' },
                specialty: { buttons: '', captions: '', navigation: '' },
                fallbacks: []
              }
            },
            // Include visual content strategy data
            visualContentStrategy: brand.visualContentStrategy || {
              colorUsage: {
                socialMediaPosts: '',
                callToActions: '',
                backgrounds: '',
                seasonalAdaptations: ''
              },
              typographyGuidelines: {
                socialMediaHeaders: '',
                bodyText: '',
                quotesTestimonials: '',
                hashtagsKeywords: ''
              },
              visualElementsToEmphasize: [],
              designConsistencyTips: [],
              brandExtensions: {
                socialTemplates: '',
                logoUsage: '',
                imageFilters: ''
              }
            },
            // Include go-to-market strategy data (NEW!)
            goToMarketStrategy: brand.goToMarketStrategy || {
              short: '',
              targetSegments: [],
              keyPersonaTypes: [],
              marketScope: 'local',
              priorityMarkets: [],
              businessModel: 'B2C',
              distributionType: 'digital'
            }
          };
          
          logger.debug('BrandForm', 'Creating brand with minimal required fields');
          
          const result = await brandCreateMutation.mutateAsync(minimalData);
                      logger.info('BrandForm', `Brand created successfully: ${brand.name}`);
          
          // Reset the AI fill flag after successful save
          setHasBeenFilledWithAI(false);
          navigate('/brand-strategy-lab');
          
        } catch (createError: any) {
          logger.error('Failed to create brand with minimal data:', JSON.stringify(createError));
          
          // Log detailed error info
          if (createError.response) {
            logger.error('Error response:', JSON.stringify({
              status: createError.response.status,
              data: createError.response.data
            }));
          }
          
          // Try with complete data as a fallback (optional, minimal should work now)
          // ... (keep fallback or remove if minimalData is now complete)
           try {
            logger.info('Attempting with full data as fallback', 'BRAND_CREATE');
            
            const fullData = {
              name: brand.name,
              description: brand.description,
              logo: brand.logo,
              website: brand.website,
              industryId: brand.industryId,
              values: brand.values,
              keywords: brand.keywords,
              languages: brand.languages,
              contentTone: brand.contentTone,
              imageStyle: brand.imageStyle,
              voice: brand.voice,
              uniqueSellingPoints: brand.uniqueSellingPoints,
              recommendedFormats: brand.recommendedFormats,
              socialMedias: brand.socialMedias,
              themes: brand.themes,
              contentGaps: brand.contentGaps,
              engagementTactics: brand.engagementTactics,
              growthOpportunities: brand.growthOpportunities,
              summary: brand.summary,
              visualIdentity: brand.visualIdentity,
              visualContentStrategy: brand.visualContentStrategy,
              goToMarketStrategy: brand.goToMarketStrategy,
              contentStrategy: contentStrategyObj,
              overview: overviewObj
            };
            
            await brandCreateMutation.mutateAsync(fullData);
            
            // Reset the AI fill flag after successful save
            setHasBeenFilledWithAI(false);
            navigate('/brand-strategy-lab');
          } catch (fullError: any) {
            logger.error('Failed to create brand with full data:', JSON.stringify(fullError));
            throw fullError;
          }
        }
      }
    } catch (error) {
      logger.error('Error saving brand:', JSON.stringify(error));
      // Show detailed error message to user
      if (error instanceof Error) {
        alert(t('brands.edit.saveErrorDetailed', `Failed to save brand: ${error.message}`));
      } else {
        alert(t('brands.edit.saveErrorGeneric', 'Failed to save brand. Please try again.'));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const updateBrand = React.useCallback((field: string, value: any) => {
    setBrand((prev: Brand) => {
      // Ensure we always have a valid brand object with all required fields
      const updatedBrand = { ...prev, [field]: value };
      
      // Ensure goToMarketStrategy is always defined
      if (!updatedBrand.goToMarketStrategy) {
        updatedBrand.goToMarketStrategy = {
          short: '',
          targetSegments: [],
          keyPersonaTypes: [],
          marketScope: 'local',
          priorityMarkets: [],
          businessModel: 'B2C',
          distributionType: 'digital'
        };
      }
      
      return updatedBrand;
    });
  }, []);

  const handleAddSocialNetwork = (platform: string, frequency: PostingFrequency) => {
    updateBrand('socialMedias', [
      ...brand.socialMedias,
      { platform, frequency }
    ]);
  };

  // --- Add handlers for adding/removing items for the refactored sections ---
  const handleAddItem = (field: string, value: string) => {
    // Handle goToMarketStrategy fields
    if (['targetSegments', 'keyPersonaTypes', 'priorityMarkets'].includes(field)) {
      const currentItems = brand.goToMarketStrategy?.[field as keyof typeof brand.goToMarketStrategy] as string[] || [];
      if (value && !currentItems.includes(value)) {
        updateBrand('goToMarketStrategy', {
          ...brand.goToMarketStrategy,
          [field]: [...currentItems, value]
        });
      }
      return;
    }
    
    // Handle regular brand fields
    const currentItems = (brand as any)[field] as string[];
    if (value && !currentItems.includes(value)) {
        updateBrand(field, [...currentItems, value]);
    }
  };

  const handleRemoveItem = (field: string, index: number) => {
    // Handle goToMarketStrategy fields
    if (['targetSegments', 'keyPersonaTypes', 'priorityMarkets'].includes(field)) {
      const currentItems = brand.goToMarketStrategy?.[field as keyof typeof brand.goToMarketStrategy] as string[] || [];
      updateBrand('goToMarketStrategy', {
        ...brand.goToMarketStrategy,
        [field]: currentItems.filter((_: any, i: number) => i !== index)
      });
      return;
    }
    
    // Handle regular brand fields
    const currentItems = (brand as any)[field] as string[];
    updateBrand(field, currentItems.filter((_: any, i: number) => i !== index));
  };
  
  const handleRemoveTag = (field: string, tagToRemove: string) => {
      const currentItems = (brand as any)[field] as string[];
      updateBrand(field, currentItems.filter((tag: string) => tag !== tagToRemove));
  };
  // --- End Add handlers ---

  // --- Color management functions ---
  const handleColorClick = (color: string, type: 'primary' | 'secondary' | 'accent' | 'neutral', index: number) => {
    setEditingColor({ color, type, index });
    setIsColorModalOpen(true);
  };

  const handleNewColor = () => {
    setEditingColor(null);
    setIsColorModalOpen(true);
  };

  const handleSaveColor = (newColor: string, type: 'primary' | 'secondary' | 'accent' | 'neutral') => {
    const colorPalette = { ...brand.visualIdentity?.colorPalette };
    
    if (editingColor) {
      // Editing existing color
      const oldType = editingColor.type;
      const oldIndex = editingColor.index;
      
      // Remove from old position
      if (colorPalette[oldType]) {
        colorPalette[oldType] = colorPalette[oldType].filter((_: string, i: number) => i !== oldIndex);
      }
    }
    
    // Add to new position
    if (!colorPalette[type]) {
      colorPalette[type] = [];
    }
    colorPalette[type].push(newColor);
    
    updateBrand('visualIdentity', {
      ...brand.visualIdentity,
      colorPalette
    });
    
    setIsColorModalOpen(false);
    setEditingColor(null);
  };

  const handleDeleteColor = (type: 'primary' | 'secondary' | 'accent' | 'neutral', index: number) => {
    console.log('Deleting color:', { type, index, currentColors: brand.visualIdentity?.colorPalette?.[type] });
    
    if (!brand.visualIdentity?.colorPalette?.[type]) {
      console.warn('No colors found for type:', type);
      return;
    }

    const colorPalette = {
      ...brand.visualIdentity.colorPalette,
      primary: [...(brand.visualIdentity.colorPalette.primary || [])],
      secondary: [...(brand.visualIdentity.colorPalette.secondary || [])],
      accent: [...(brand.visualIdentity.colorPalette.accent || [])],
      neutral: [...(brand.visualIdentity.colorPalette.neutral || [])]
    };
    
    // Remove the color at the specified index
    colorPalette[type] = colorPalette[type].filter((_: string, i: number) => i !== index);
    
    console.log('New color palette:', colorPalette);
    
    updateBrand('visualIdentity', {
      ...brand.visualIdentity,
      colorPalette
    });
  };
  // --- End Color management functions ---

  // --- Font management functions ---
  const handleFontClick = (font: string, type: 'heading' | 'body' | 'buttons' | 'captions' | 'navigation', subtype?: 'h1' | 'h2' | 'h3' | 'primary' | 'secondary') => {
    setEditingFont({ font, type, subtype });
    setFontModal(true);
  };

  const handleNewFont = () => {
    setEditingFont(null);
    setFontModal(true);
  };

  const handleSaveFont = (newFont: string, type: 'heading' | 'body' | 'buttons' | 'captions' | 'navigation', subtype?: 'h1' | 'h2' | 'h3' | 'primary' | 'secondary') => {
    const newTypography = { ...brand.visualIdentity?.typography };
    
    if (type === 'heading' && subtype) {
      newTypography.headings = {
        ...newTypography.headings,
        [subtype]: newFont
      };
    } else if (type === 'body' && subtype) {
      newTypography.body = {
        ...newTypography.body,
        [subtype]: newFont
      };
    } else if (type === 'buttons') {
      newTypography.specialty = {
        ...newTypography.specialty,
        buttons: newFont
      };
    } else if (type === 'captions') {
      newTypography.specialty = {
        ...newTypography.specialty,
        captions: newFont
      };
    } else if (type === 'navigation') {
      newTypography.specialty = {
        ...newTypography.specialty,
        navigation: newFont
      };
    }

    updateBrand('visualIdentity', {
      ...brand.visualIdentity,
      typography: newTypography
    });

    setFontModal(false);
    setEditingFont(null);
  };

  const handleDeleteFont = (type: 'heading' | 'body' | 'buttons' | 'captions' | 'navigation', subtype?: 'h1' | 'h2' | 'h3' | 'primary' | 'secondary') => {
    const newTypography = { ...brand.visualIdentity?.typography };
    
    if (type === 'heading' && subtype) {
      newTypography.headings = {
        ...newTypography.headings,
        [subtype]: ''
      };
    } else if (type === 'body' && subtype) {
      newTypography.body = {
        ...newTypography.body,
        [subtype]: ''
      };
    } else if (type === 'buttons') {
      newTypography.specialty = {
        ...newTypography.specialty,
        buttons: ''
      };
    } else if (type === 'captions') {
      newTypography.specialty = {
        ...newTypography.specialty,
        captions: ''
      };
    } else if (type === 'navigation') {
      newTypography.specialty = {
        ...newTypography.specialty,
        navigation: ''
      };
    }

    updateBrand('visualIdentity', {
      ...brand.visualIdentity,
      typography: newTypography
    });
  };
  // --- End Font management functions ---

  // --- Refresh data function ---
  const handleRefreshData = async () => {
    if (!brand.website) {
      alert(t('brands.edit.refresh.noWebsite', 'No website URL found to analyze.'));
      return;
    }

    try {
      setIsRefreshing(true);
      setIsRefreshModalOpen(false);

      // Get the current user's ID token
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      let websiteUrl = brand.website;
      if (!/^https?:\/\//i.test(websiteUrl)) {
        websiteUrl = `https://${websiteUrl}`;
      }

      const response = await fetch('https://api.socialness.ai/ai-coach/brand/analyze?WithVisual=true&fast=true', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Origin': 'https://socialness.ai'
        },
        body: JSON.stringify({
          url: websiteUrl,
          language: i18n.language.split('-')[0].toLowerCase()
        })
      });

      // Check if response is ok before trying to parse JSON
      if (!response.ok) {
        let errorMessage = `API returned status ${response.status}`;
        
        try {
          // Try to parse as JSON first
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (jsonError) {
          // If not JSON, try to get text content
          try {
            const errorText = await response.text();
            // If it's an HTML page (like a 503 error page), provide a more user-friendly message
            if (errorText.includes('<!DOCTYPE') || errorText.includes('<html')) {
              errorMessage = t('brands.edit.refresh.serviceUnavailable', 'The analysis service is temporarily unavailable. Please try again in a few minutes.');
            } else {
              errorMessage = errorText || errorMessage;
            }
          } catch (textError) {
            // If we can't read the response at all, use the status code
            errorMessage = t('brands.edit.refresh.temporaryError', 'Service temporarily unavailable ({status}). Please try again later.').replace('{status}', response.status.toString());
          }
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();

      if (!data.analysis) {
        throw new Error(t('brands.edit.refresh.invalidResponse', 'Invalid response from analysis'));
      }

      // Use our mapping function to convert API response to BrandForm format
      const refreshedData = mapAnalysisToBrand(data, websiteUrl);

      // Keep the existing name and ID, but update everything else
      const updatedBrand = {
        ...refreshedData,
        id: brand.id,
        name: brand.name // Keep the current name, don't overwrite it
      };

              logger.info('BrandForm', `Brand data refreshed for: ${updatedBrand.name}`);
      setBrand(updatedBrand as Brand);
      
      setIsRefreshSuccessModalOpen(true);

    } catch (error) {
      console.error('Error refreshing brand data:', error);
      alert(error instanceof Error ? error.message : t('brands.edit.refresh.genericError', 'Failed to refresh brand data. Please try again.'));
    } finally {
      setIsRefreshing(false);
    }
  };
  // --- End Refresh data function ---

  // --- Fill with AI function ---
  const handleFillWithAI = async (websiteUrl: string) => {
    try {
      setIsRefreshing(true);
      setIsUrlInputModalOpen(false);

      // Get the current user's ID token
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      let formattedUrl = websiteUrl;
      if (!/^https?:\/\//i.test(formattedUrl)) {
        formattedUrl = `https://${formattedUrl}`;
      }

      const response = await fetch('https://api.socialness.ai/ai-coach/brand/analyze?WithVisual=true&fast=true', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Origin': 'https://socialness.ai'
        },
        body: JSON.stringify({
          url: formattedUrl,
          language: i18n.language.split('-')[0].toLowerCase()
        })
      });

      // Check if response is ok before trying to parse JSON
      if (!response.ok) {
        let errorMessage = `API returned status ${response.status}`;
        
        try {
          // Try to parse as JSON first
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (jsonError) {
          // If not JSON, try to get text content
          try {
            const errorText = await response.text();
            // If it's an HTML page (like a 503 error page), provide a more user-friendly message
            if (errorText.includes('<!DOCTYPE') || errorText.includes('<html')) {
              errorMessage = t('brands.edit.fillWithAI.serviceUnavailable', 'The analysis service is temporarily unavailable. Please try again in a few minutes.');
            } else {
              errorMessage = errorText || errorMessage;
            }
          } catch (textError) {
            // If we can't read the response at all, use the status code
            errorMessage = t('brands.edit.fillWithAI.temporaryError', 'Service temporarily unavailable ({status}). Please try again later.').replace('{status}', response.status.toString());
          }
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();

      if (!data.analysis) {
        throw new Error(t('brands.edit.fillWithAI.invalidResponse', 'Invalid response from analysis'));
      }

      // Use our mapping function to convert API response to BrandForm format
      const analysisData = mapAnalysisToBrand(data, formattedUrl);

      // Keep the existing name and ID, but update everything else
      const updatedBrand = {
        ...analysisData,
        id: brand.id,
        name: brand.name, // Keep the current name if it exists
        website: formattedUrl // Update the website URL
      };

              logger.info('BrandForm', `Brand data filled with AI for: ${updatedBrand.name}`);
      setBrand(updatedBrand as Brand);
      
      // Don't update originalBrand so changes are detected when saving
      // Set flag to force save even if change detection fails
      setHasBeenFilledWithAI(true);
      logger.debug('BrandForm', 'Brand state updated after AI fill - originalBrand left unchanged for change detection');
      
      setIsRefreshSuccessModalOpen(true);

    } catch (error) {
      console.error('Error filling brand data with AI:', error);
      alert(error instanceof Error ? error.message : t('brands.edit.fillWithAI.genericError', 'Failed to analyze website. Please try again.'));
    } finally {
      setIsRefreshing(false);
    }
  };
  // --- End Fill with AI function ---

  // Handle logo file upload
  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check file type
      if (!file.type.startsWith('image/')) {
        alert(t('brands.edit.brandIdentity.logo.invalidType', 'Please select a valid image file'));
        return;
      }
      
      // Check file size (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert(t('brands.edit.brandIdentity.logo.tooLarge', 'File size must be less than 5MB'));
        return;
      }
      
      // Convert to data URL for preview
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string;
        updateBrand('logo', dataUrl);
      };
      reader.readAsDataURL(file);
    }
    
    // Clear the input value to allow selecting the same file again
    event.target.value = '';
  };

  const tabs = React.useMemo(() => [
    { id: 'branding', label: t('brands.sections.branding', 'Branding'), icon: React.createElement(Palette, { className: "w-4 h-4" }) },
    { id: 'marketing', label: t('brands.sections.marketingStrategy', 'Marketing Strategy'), icon: React.createElement(Megaphone, { className: "w-4 h-4" }) },
    // { id: 'styles', label: t('brands.sections.brandStyles', 'Brand Styles'), icon: React.createElement(Palette, { className: "w-4 h-4" }) }, // Temporarily hidden
    { id: 'content', label: t('brands.sections.contentStrategy', 'Content Strategy'), icon: React.createElement(Lightbulb, { className: "w-4 h-4" }) },
    { id: 'summary', label: t('brands.sections.recap', 'Summary'), icon: React.createElement(FileText, { className: "w-4 h-4" }) }
  ], [t]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white lg:ml-16 pt-16 lg:pt-0">
        <div className="w-[95%] mx-auto py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
            <div className="space-y-6">
              <div className="h-40 bg-gray-200 rounded"></div>
              <div className="h-40 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Handle error state without early return to avoid hook violations
  if (brandError) {
    return (
      <div className="min-h-screen bg-white lg:ml-16 pt-16 lg:pt-0">
        <div className="w-[95%] mx-auto py-8">
          <div className="text-center text-red-600">
            {t('brands.edit.loadError', 'Failed to load brand. Please try again later.')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white lg:ml-16 pt-16 lg:pt-0 pb-8">
      <div className="w-[95%] mx-auto">
        <div className="py-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/brand-strategy-lab')}
                className="p-2 rounded-lg hover:text-primary-600 transition-colors"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-3xl font-semibold text-gray-900 font-title">
                  {isEditMode ? brand.name : t('brands.create.title', 'Create Brand')}
                </h1>
                <p className="text-sm text-gray-500">
                  {isEditMode ? t('brands.edit.subtitle', 'Update brand information and strategy') : t('brands.create.subtitle', 'Set up a new brand and its social media strategy')}
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              {/* Refresh Data Button - Only show in edit mode and if website exists */}
              {isEditMode && brand.website && (
                <button
                  onClick={() => setIsRefreshModalOpen(true)}
                  disabled={isRefreshing || isSaving}
                  className={`inline-flex items-center px-4 py-2 rounded-lg transition-colors border ${
                    isRefreshing || isSaving
                      ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed' 
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                  }`}
                >
                  {isRefreshing ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      {t('brands.edit.refresh.refreshing', 'Refreshing...')}
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-5 h-5 mr-2" />
                      {t('brands.edit.refresh.button', 'Refresh Data')}
                    </>
                  )}
                </button>
              )}

              {/* Fill with AI Button - Only show when there's no website */}
              {(!brand.website || brand.website.trim() === '') && (
                <button
                  onClick={() => setIsFillWithAIModalOpen(true)}
                  disabled={isRefreshing || isSaving}
                  className={`inline-flex items-center px-4 py-2 rounded-lg transition-colors border ${
                    isRefreshing || isSaving
                      ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed' 
                      : 'bg-purple-50 text-purple-700 border-purple-300 hover:bg-purple-100 hover:border-purple-400'
                  }`}
                >
                  {isRefreshing ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      {t('brands.edit.fillWithAI.analyzing', 'Analyzing...')}
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      {t('brands.edit.fillWithAI.button', 'Fill with AI')}
                    </>
                  )}
                </button>
              )}
              
              {/* Save Button */}
              <button
                onClick={handleSave}
                disabled={isSaving || isRefreshing}
                className={`inline-flex items-center px-4 py-2 rounded-lg transition-colors ${
                  isSaving || isRefreshing
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-primary-600 text-white hover:bg-primary-700'
                }`}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {t('common.savingInProgress', 'Saving...')} 
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5 mr-2" />
                    {isEditMode ? t('common.save', 'Save') : t('brands.create.submit', 'Save Brand')}
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="mb-6">
            <TabGroup
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          </div>

          {activeTab === 'branding' ? (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                {/* Brand Identity - 2/3 width */}
                <div className="col-span-1 lg:col-span-2 bg-white rounded-xl border border-black shadow-lg p-6">
                  <h2 className="text-xl font-semibold mb-6">{t('brands.edit.brandIdentity.title', 'Brand Identity')}</h2>
                  <div className="space-y-6">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                      <div className="w-20 h-20">
                        <img
                          src={brand.logo || 'https://via.placeholder.com/80'}
                          alt={brand.name}
                          className="w-full h-full object-contain rounded-lg"
                        />
                        <label
                          htmlFor="file-upload"
                          className="mt-2 flex justify-center text-xs text-primary-600 hover:text-primary-500 cursor-pointer"
                        >
                          <Upload className="w-3 h-3 mr-1" />
                          {t('brands.edit.brandIdentity.logo.change', 'Change Logo')}
                          <input id="file-upload" name="file-upload" type="file" accept="image/*" className="sr-only" onChange={handleLogoChange} />
                        </label>
                      </div>
                      <div className="flex-1 space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('brands.edit.brandIdentity.name', 'Brand Name')} <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={brand.name}
                            onChange={(e) => updateBrand('name', e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('brands.edit.brandIdentity.website', 'Website URL')}
                          </label>
                          <div className="flex items-center gap-2">
                            <Globe className="w-5 h-5 text-gray-400" />
                            <input
                              type="url"
                              value={brand.website}
                              onChange={(e) => updateBrand('website', e.target.value)}
                              className="flex-1 rounded-lg border border-gray-300 px-3 py-2"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('brands.edit.brandIdentity.description', 'Brand Description')}
                      </label>
                      <textarea
                        value={brand.description}
                        onChange={(e) => updateBrand('description', e.target.value)}
                        rows={4}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2"
                      />
                    </div>
                  </div>
                </div>

                {/* Brand Tone - 1/3 width */}
                <div className="bg-white rounded-xl border border-black shadow-lg p-6">
                  <h2 className="text-xl font-semibold mb-6">{t('brands.edit.brandTone.title', 'Brand Tone')}</h2>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('brands.edit.brandTone.contentTone', 'Content Tone')}
                      </label>
                      <ContentToneSelect 
                        value={brand.contentTone}
                        onChange={(value) => updateBrand('contentTone', value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('brands.edit.brandTone.languages', 'Languages')}
                      </label>
                      <LanguageInput
                        languages={brand.languages}
                        onLanguagesChange={(languages) => updateBrand('languages', languages)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('brands.edit.brandTone.imageStyle', 'Image Style')}
                      </label>
                      <ImageStyleSelect
                        value={brand.imageStyle}
                        onChange={(value) => updateBrand('imageStyle', value)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Brand Guidelines - 50% width */}
                <div className="bg-white rounded-xl border border-black shadow-lg p-6">
                  <h2 className="text-xl font-semibold mb-6">{t('brands.edit.brandGuidelines.title', 'Brand Guidelines')}</h2>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('brands.edit.brandGuidelines.values', 'Brand Values')}
                      </label>
                      {/* Assuming TagInput is still desired for 'values' or remove if AddItemInput should be used */}
                      <TagInput
                        tags={brand.values}
                        onTagsChange={(values) => updateBrand('values', values)}
                        placeholder={t('brands.edit.brandGuidelines.values', 'Add a brand value')}
                        tagClassName="bg-primary-50 text-primary-700"
                         onInputKeyDown={(e) => { // Keep adding on enter for this tag input if needed
                           if (e.key === 'Enter') {
                             const input = (e.target as HTMLInputElement).value.trim();
                             if (input && !brand.values.includes(input)) {
                               updateBrand('values', [...brand.values, input]);
                               (e.target as HTMLInputElement).value = '';
                               e.preventDefault();
                             }
                           }
                         }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('brands.edit.brandGuidelines.voiceDescription', 'Brand Voice Description')}
                      </label>
                      <textarea
                        value={brand.voice}
                        onChange={(e) => updateBrand('voice', e.target.value)}
                        rows={4}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2"
                      />
                    </div>
                    <div>
                      <SocialFrequencyDisplay
                        networks={brand.socialMedias}
                        onAddNetwork={() => setIsAddNetworkModalOpen(true)}
                        isPersonaForm={false}
                      />
                    </div>
                  </div>
                </div>

                {/* Brand Strategy - 50% width */}
                <div className="bg-white rounded-xl border border-black shadow-lg p-6">
                  <h2 className="text-xl font-semibold mb-6">{t('brands.edit.brandStrategy.title', 'Brand Strategy')}</h2>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('brands.edit.brandStrategy.industry', 'Industry')}
                      </label>
                      <IndustrySelect
                        value={brand.industryId}
                        onChange={(value) => updateBrand('industryId', value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('brands.edit.brandStrategy.usp', 'Unique Selling Points')}
                      </label>
                       {/* Assuming TagInput is still desired for 'usp' or remove if AddItemInput should be used */}
                      <TagInput
                        tags={brand.uniqueSellingPoints}
                        onTagsChange={(points) => updateBrand('uniqueSellingPoints', points)}
                        icon={<Megaphone className="w-3 h-3" />}
                        placeholder={t('brands.edit.brandStrategy.usp', 'Add a unique selling point')}
                        tagClassName="bg-primary-50 text-primary-700"
                         onInputKeyDown={(e) => { // Keep adding on enter for this tag input if needed
                           if (e.key === 'Enter') {
                             const input = (e.target as HTMLInputElement).value.trim();
                             if (input && !brand.uniqueSellingPoints.includes(input)) {
                               updateBrand('uniqueSellingPoints', [...brand.uniqueSellingPoints, input]);
                               (e.target as HTMLInputElement).value = '';
                               e.preventDefault();
                             }
                           }
                         }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('brands.edit.brandStrategy.contentFormats', 'Content Formats')}
                      </label>
                       {/* Assuming TagInput is still desired for 'formats' or remove if AddItemInput should be used */}
                      <TagInput
                        tags={brand.recommendedFormats}
                        onTagsChange={(formats) => updateBrand('recommendedFormats', formats)}
                        icon={<MessageSquare className="w-3 h-3" />}
                        placeholder={t('brands.edit.brandStrategy.contentFormats', 'Add a content format')}
                        tagClassName="bg-primary-50 text-primary-700"
                         onInputKeyDown={(e) => { // Keep adding on enter for this tag input if needed
                           if (e.key === 'Enter') {
                             const input = (e.target as HTMLInputElement).value.trim();
                             if (input && !brand.recommendedFormats.includes(input)) {
                               updateBrand('recommendedFormats', [...brand.recommendedFormats, input]);
                               (e.target as HTMLInputElement).value = '';
                               e.preventDefault();
                             }
                           }
                         }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : activeTab === 'marketing' ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Go-To-Market Strategy Card */}
              <div className="lg:col-span-2 bg-white rounded-xl border border-black shadow-lg p-6 space-y-3">
                <h2 className="text-xl font-semibold mb-2 flex items-center gap-2">
                  <Target className="w-5 h-5 text-blue-500" />
                  {t('brands.edit.marketingStrategy.gtm.title', 'Go-To-Market Strategy')}
                </h2>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('brands.edit.marketingStrategy.gtm.short', 'Short Description')}
                  </label>
                  <textarea
                    value={brand.goToMarketStrategy?.short || ''}
                    onChange={(e) => updateBrand('goToMarketStrategy', { 
                      ...(brand.goToMarketStrategy || emptyBrand.goToMarketStrategy), 
                      short: e.target.value 
                    })}
                    rows={4}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Target className="w-4 h-4 text-blue-500" />
                      {t('brands.edit.marketingStrategy.gtm.targetSegments', 'Target Segments')} <span className="text-red-500">*</span>
                    </h3>
                    <AddItemInput
                      placeholder={t('brands.edit.marketingStrategy.gtm.targetSegmentsPlaceholder', 'Add target segment')}
                      onAdd={(value) => handleAddItem('targetSegments', value)}
                      useLargeButton={true}
                      existingItems={brand.goToMarketStrategy?.targetSegments || []}
                    />
                    {Array.isArray(brand.goToMarketStrategy?.targetSegments) && brand.goToMarketStrategy.targetSegments.length > 0 && (
                      <ul className="space-y-2 pt-2">
                        {brand.goToMarketStrategy.targetSegments.map((item: string, index: number) => (
                          <li key={`targetSegment-${item}-${index}`} className="flex items-start gap-2 group">
                            <Target className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                            <span className="flex-1 text-sm text-gray-700">{item}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveItem('targetSegments', index)}
                              className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600 transition-opacity"
                              aria-label={`${t('common.delete', 'Delete')} ${item}`}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Users className="w-4 h-4 text-purple-500" />
                      {t('brands.edit.marketingStrategy.personas.types', 'Persona Types')}
                    </h3>
                    <AddItemInput
                      placeholder={t('brands.edit.marketingStrategy.personas.typesPlaceholder', 'Add persona type')}
                      onAdd={(value) => handleAddItem('keyPersonaTypes', value)}
                      useLargeButton={true}
                      existingItems={brand.goToMarketStrategy?.keyPersonaTypes || []}
                    />
                    {Array.isArray(brand.goToMarketStrategy?.keyPersonaTypes) && brand.goToMarketStrategy.keyPersonaTypes.length > 0 && (
                      <ul className="space-y-2 pt-2">
                        {brand.goToMarketStrategy.keyPersonaTypes.map((item: string, index: number) => (
                          <li key={`personaType-${item}-${index}`} className="flex items-start gap-2 group">
                            <Users className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                            <span className="flex-1 text-sm text-gray-700">{item}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveItem('keyPersonaTypes', index)}
                              className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600 transition-opacity"
                              aria-label={`${t('common.delete', 'Delete')} ${item}`}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('brands.edit.marketingStrategy.businessModel', 'Business Model')} <span className="text-red-500">*</span>
                  </label>
                  <BusinessModelSelect
                    value={brand.goToMarketStrategy?.businessModel || 'B2C'}
                    onChange={(value) => updateBrand('goToMarketStrategy', { 
                      ...(brand.goToMarketStrategy || emptyBrand.goToMarketStrategy), 
                      businessModel: value 
                    })}
                  />
                </div>
              </div>

              {/* Market Scope Card */}
              <div className="bg-white rounded-xl border border-black shadow-lg p-6">
                <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-green-500" />
                  {t('brands.edit.marketingStrategy.marketScope.title', 'Market Scope')}
                </h2>
                <div className="space-y-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('brands.edit.marketingStrategy.marketScope.scope', 'Market Scope')} <span className="text-red-500">*</span>
                    </label>
                    <MarketScopeSelect
                      value={brand.goToMarketStrategy?.marketScope || 'local'}
                      onChange={(value) => updateBrand('goToMarketStrategy', { 
                        ...(brand.goToMarketStrategy || emptyBrand.goToMarketStrategy), 
                        marketScope: value 
                      })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('brands.edit.marketingStrategy.distributionType', 'Distribution Type')}
                    </label>
                    <DistributionTypeSelect
                      value={brand.goToMarketStrategy?.distributionType || 'digital'}
                      onChange={(value) => updateBrand('goToMarketStrategy', { 
                        ...(brand.goToMarketStrategy || emptyBrand.goToMarketStrategy), 
                        distributionType: value 
                      })}
                    />
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Globe className="w-4 h-4 text-green-500" />
                    {t('brands.edit.marketingStrategy.marketScope.priorityMarkets', 'Priority Markets')} <span className="text-red-500">*</span>
                  </h3>
                  <AddItemInput
                    placeholder={t('brands.edit.marketingStrategy.marketScope.priorityMarketsPlaceholder', 'Add priority market')}
                    onAdd={(value) => handleAddItem('priorityMarkets', value)}
                    useLargeButton={true}
                    existingItems={brand.goToMarketStrategy?.priorityMarkets || []}
                  />
                  {Array.isArray(brand.goToMarketStrategy?.priorityMarkets) && brand.goToMarketStrategy.priorityMarkets.length > 0 && (
                    <ul className="space-y-2 pt-2">
                      {brand.goToMarketStrategy.priorityMarkets.map((item: string, index: number) => (
                        <li key={`priorityMarket-${item}-${index}`} className="flex items-start gap-2 group">
                          <Globe className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="flex-1 text-sm text-gray-700">{item}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveItem('priorityMarkets', index)}
                            className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600 transition-opacity"
                            aria-label={`${t('common.delete', 'Delete')} ${item}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          ) : activeTab === 'content' ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* --- Content Themes --- */}
              <div className="bg-white rounded-xl border border-black shadow-lg p-6 space-y-3">
                <h2 className="text-xl font-semibold mb-2 flex items-center gap-2"> {/* Reduced margin */}
                  <Lightbulb className="w-5 h-5 text-yellow-500" />
                  {t('brands.edit.contentStrategy.themes.title', 'Content Themes')}
                </h2>
                <AddItemInput
                  placeholder={t('brands.edit.contentStrategy.themes.placeholder', 'Add a content theme')}
                  onAdd={(value) => handleAddItem('themes', value)}
                  useLargeButton={true} // Use large button style for consistency
                  existingItems={brand.themes}
                  showEnterHint={true}
                />
                 {/* Render themes list (like TagInput) */}
                <div className="flex flex-wrap gap-2">
                  {Array.isArray(brand.themes) && brand.themes.map((theme: string, index: number) => (
                    <span
                      key={`theme-${theme}-${index}`}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-yellow-50 text-yellow-700" // Use theme color
                    >
                      <Lightbulb className="w-4 h-4 mr-1" /> {/* Add icon */}
                      {theme}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag('themes', theme)} // Use specific remove handler
                        className="ml-1 hover:text-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* --- Content Gaps --- */}
              <div className="bg-white rounded-xl border border-black shadow-lg p-6 space-y-3">
                <h2 className="text-xl font-semibold mb-2 flex items-center gap-2">
                  <Target className="w-5 h-5 text-red-500" />
                  {t('brands.edit.contentStrategy.gaps.title', 'Content Gaps')}
                </h2>
                 <AddItemInput
                  placeholder={t('brands.edit.contentStrategy.gaps.placeholder', 'Add a content gap to fill')}
                  onAdd={(value) => handleAddItem('contentGaps', value)}
                  useLargeButton={true} // Use large button style
                  existingItems={brand.contentGaps}
                  showEnterHint={true}
                />
                {/* Render content gaps list (like ListInput) */}
                 {Array.isArray(brand.contentGaps) && brand.contentGaps.length > 0 && (
                    <ul className="space-y-2 pt-2"> {/* Added padding top */}
                      {brand.contentGaps.map((item: string, index: number) => (
                        <li key={`contentGap-${item}-${index}`} className="flex items-start gap-2 group">
                          <Target className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" /> {/* Adjusted icon style */}
                          <span className="flex-1 text-sm text-gray-700">{item}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveItem('contentGaps', index)}
                            className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600 transition-opacity"
                            aria-label={`${t('common.delete', 'Delete')} ${item}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
              </div>

              {/* --- Engagement Strategy --- */}
              <div className="bg-white rounded-xl border border-black shadow-lg p-6 space-y-3">
                <h2 className="text-xl font-semibold mb-2 flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-500" />
                  {t('brands.edit.contentStrategy.engagement.title', 'Engagement Strategy')}
                </h2>
                <AddItemInput
                  placeholder={t('brands.edit.contentStrategy.engagement.placeholder', 'Add an engagement tactic')}
                  onAdd={(value) => handleAddItem('engagementTactics', value)}
                  useLargeButton={true} // Use large button style
                  existingItems={brand.engagementTactics}
                  showEnterHint={true}
                />
                 {/* Render engagement tactics list (like ListInput) */}
                 {Array.isArray(brand.engagementTactics) && brand.engagementTactics.length > 0 && (
                    <ul className="space-y-2 pt-2">
                      {brand.engagementTactics.map((item: string, index: number) => (
                        <li key={`engagementTactic-${item}-${index}`} className="flex items-start gap-2 group">
                          <Users className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                          <span className="flex-1 text-sm text-gray-700">{item}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveItem('engagementTactics', index)}
                            className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600 transition-opacity"
                            aria-label={`${t('common.delete', 'Delete')} ${item}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
              </div>

              {/* --- Growth Opportunities --- */}
              <div className="bg-white rounded-xl border border-black shadow-lg p-6 space-y-3">
                <h2 className="text-xl font-semibold mb-2 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  {t('brands.edit.contentStrategy.growth.title', 'Growth Opportunities')}
                </h2>
                 <AddItemInput
                  placeholder={t('brands.edit.contentStrategy.growthOpportunities.placeholder', 'Add a growth opportunity')}
                  onAdd={(value) => handleAddItem('growthOpportunities', value)}
                  useLargeButton={true} // Use large button style
                  existingItems={brand.growthOpportunities}
                  showEnterHint={true}
                />
                 {/* Render growth opportunities list (like ListInput) */}
                  {Array.isArray(brand.growthOpportunities) && brand.growthOpportunities.length > 0 && (
                    <ul className="space-y-2 pt-2">
                      {brand.growthOpportunities.map((item: string, index: number) => (
                        <li key={`growthOpportunity-${item}-${index}`} className="flex items-start gap-2 group">
                          <TrendingUp className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="flex-1 text-sm text-gray-700">{item}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveItem('growthOpportunities', index)}
                            className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600 transition-opacity"
                            aria-label={`${t('common.delete', 'Delete')} ${item}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
              </div>
            </div>
                    ) : activeTab === 'styles' ? (
            <div className="space-y-6">
              {/* Top row: Brand Colors and Brand Fonts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Brand Colors */}
                <div className="bg-white rounded-xl border border-black shadow-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold flex items-center gap-2">
                    <Palette className="w-5 h-5 text-primary-600" />
                    {t('brands.edit.brandStyles.colors.title', 'Brand Colors')}
                  </h2>
                  <button
                    onClick={handleNewColor}
                    className="inline-flex items-center px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    {t('brands.edit.brandStyles.colors.addColor', 'Add Color')}
                  </button>
                </div>
                <div className="space-y-4">
                  {/* Color Palette Display - All colors in one row */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {/* Primary Colors */}
                    {brand.visualIdentity?.colorPalette?.primary?.map((color: string, index: number) => {
                      const colorName = getColorName(color, 'Primary');
                      return (
                        <div key={`primary-${color}-${index}`} className="text-center group">
                          <div
                            className="w-full aspect-square rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-200 cursor-pointer mb-2 relative"
                            style={{ backgroundColor: color }}
                            onClick={() => handleColorClick(color, 'primary', index)}
                          >
                            <div className="absolute top-2 right-2">
                              <span className="inline-block px-2 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs text-white/80 font-medium">
                                Main
                              </span>
                            </div>
                            <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                              <button
                                onClick={(e) => {
                                  console.log('Delete button clicked!', { type: 'primary', index });
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleDeleteColor('primary', index);
                                }}
                                className="w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors hover:scale-110"
                                title="Supprimer cette couleur"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
                              <Edit3 className="w-5 h-5 text-white" />
                            </div>
                          </div>
                          <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">PRIMARY</div>
                          <div className="text-sm font-semibold text-gray-800 mb-1">{colorName}</div>
                          <div className="text-xs font-mono font-medium text-gray-600">{color.toUpperCase()}</div>
                        </div>
                      );
                    })}
                    
                    {/* Secondary Colors */}
                    {brand.visualIdentity?.colorPalette?.secondary?.map((color: string, index: number) => {
                      const colorName = getColorName(color, 'Secondary');
                      return (
                        <div key={`secondary-${color}-${index}`} className="text-center group">
                          <div
                            className="w-full aspect-square rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-200 cursor-pointer mb-2 relative"
                            style={{ backgroundColor: color }}
                            onClick={() => handleColorClick(color, 'secondary', index)}
                          >
                            <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                              <button
                                onClick={(e) => {
                                  console.log('Delete button clicked!', { type: 'secondary', index });
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleDeleteColor('secondary', index);
                                }}
                                className="w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors hover:scale-110"
                                title="Supprimer cette couleur"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
                              <Edit3 className="w-5 h-5 text-white" />
                            </div>
                          </div>
                          <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">SECONDARY</div>
                          <div className="text-sm font-semibold text-gray-800 mb-1">{colorName}</div>
                          <div className="text-xs font-mono font-medium text-gray-600">{color.toUpperCase()}</div>
                        </div>
                      );
                    })}
                    
                    {/* Accent Colors */}
                    {brand.visualIdentity?.colorPalette?.accent?.map((color: string, index: number) => {
                      const colorName = getColorName(color, 'Accent');
                      return (
                        <div key={`accent-${color}-${index}`} className="text-center group">
                          <div
                            className="w-full aspect-square rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-200 cursor-pointer mb-2 relative"
                            style={{ backgroundColor: color }}
                            onClick={() => handleColorClick(color, 'accent', index)}
                          >
                            <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                              <button
                                onClick={(e) => {
                                  console.log('Delete button clicked!', { type: 'accent', index });
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleDeleteColor('accent', index);
                                }}
                                className="w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors hover:scale-110"
                                title="Supprimer cette couleur"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
                              <Edit3 className="w-5 h-5 text-white" />
                            </div>
                          </div>
                          <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">ACCENT</div>
                          <div className="text-sm font-semibold text-gray-800 mb-1">{colorName}</div>
                          <div className="text-xs font-mono font-medium text-gray-600">{color.toUpperCase()}</div>
                        </div>
                      );
                    })}
                    
                    {/* Neutral Colors */}
                    {brand.visualIdentity?.colorPalette?.neutral?.map((color: string, index: number) => {
                      const colorName = getColorName(color, 'Neutral');
                      return (
                        <div key={`neutral-${color}-${index}`} className="text-center group">
                          <div
                            className="w-full aspect-square rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-200 cursor-pointer mb-2 relative"
                            style={{ backgroundColor: color }}
                            onClick={() => handleColorClick(color, 'neutral', index)}
                          >
                            <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                              <button
                                onClick={(e) => {
                                  console.log('Delete button clicked!', { type: 'neutral', index });
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleDeleteColor('neutral', index);
                                }}
                                className="w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors hover:scale-110"
                                title="Supprimer cette couleur"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
                              <Edit3 className="w-5 h-5 text-white" />
                            </div>
                          </div>
                          <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">NEUTRAL</div>
                          <div className="text-sm font-semibold text-gray-800 mb-1">{colorName}</div>
                          <div className="text-xs font-mono font-medium text-gray-600">{color.toUpperCase()}</div>
                        </div>
                      );
                    })}
                  </div>

                  {/* No colors fallback */}
                  {(!brand.visualIdentity?.colorPalette?.primary || brand.visualIdentity.colorPalette.primary.length === 0) &&
                   (!brand.visualIdentity?.colorPalette?.secondary || brand.visualIdentity.colorPalette.secondary.length === 0) &&
                   (!brand.visualIdentity?.colorPalette?.accent || brand.visualIdentity.colorPalette.accent.length === 0) && (
                    <div className="text-center py-8 text-gray-500">
                      <Palette className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm">Aucune couleur définie</p>
                    </div>
                  )}
                  
                  {/* Recommendations */}
                  {brand.visualIdentity?.colorPalette?.recommended?.improvements && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-purple-500" />
                        {t('brands.edit.brandStyles.colors.recommendations', 'Color Recommendations')}
                      </label>
                      <div className="text-xs text-gray-600 bg-purple-50 p-3 rounded-lg">
                        {brand.visualIdentity.colorPalette.recommended.improvements}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Brand Fonts */}
              <div className="bg-white rounded-xl border border-black shadow-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary-600" />
                    {t('brands.edit.brandStyles.fonts.title', 'Brand Fonts')}
                  </h2>
                  <button
                    onClick={handleNewFont}
                    className="inline-flex items-center px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    {t('brands.edit.brandStyles.fonts.addFont', 'Add Font')}
                  </button>
                </div>
                <div className="space-y-6">
                  {/* Font Display Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Heading Font */}
                    {brand.visualIdentity?.typography?.headings?.h1 && (
                      <div className="space-y-3">
                        <div className="text-sm font-medium text-gray-700">Heading</div>
                        <div className="bg-gray-50 p-4 rounded-lg relative group cursor-pointer hover:bg-gray-100 transition-colors"
                             onClick={() => handleFontClick(brand.visualIdentity.typography.headings.h1, 'heading', 'h1')}>
                          {/* Delete button */}
                          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDeleteFont('heading', 'h1');
                              }}
                              className="w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors hover:scale-110"
                              title="Supprimer cette police"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                          
                          {/* Edit overlay */}
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-lg flex items-center justify-center transition-all">
                            <div className="opacity-0 group-hover:opacity-100 bg-white rounded-full p-2 shadow-md transition-opacity">
                              <Edit3 className="w-4 h-4 text-gray-600" />
                            </div>
                          </div>
                          
                          <div 
                            className="text-2xl font-bold mb-3 relative z-0"
                            style={{ fontFamily: `"${brand.visualIdentity.typography.headings.h1}", serif` }}
                          >
                            {brand.visualIdentity.typography.headings.h1}
                          </div>
                          <div className="flex items-center gap-2 relative z-0">
                            {(() => {
                              const fontInfo = getGoogleFontInfo(brand.visualIdentity.typography.headings.h1);
                              return fontInfo.isGoogleFont ? (
                                <>
                                  <div className="flex items-center gap-1">
                                    <img 
                                      src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" 
                                      alt="Google" 
                                      className="w-4 h-4" 
                                    />
                                    <span className="text-xs text-gray-500">Google</span>
                                  </div>
                                  <span className="text-xs text-gray-500">{fontInfo.styles} styles</span>
                                  <span className="text-xs text-gray-500">Free</span>
                                </>
                              ) : (
                                <span className="text-xs text-gray-500">System Font</span>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Body Font */}
                    {brand.visualIdentity?.typography?.body?.primary && (
                      <div className="space-y-3">
                        <div className="text-sm font-medium text-gray-700">Body</div>
                        <div className="bg-gray-50 p-4 rounded-lg relative group cursor-pointer hover:bg-gray-100 transition-colors"
                             onClick={() => handleFontClick(brand.visualIdentity.typography.body.primary, 'body', 'primary')}>
                          {/* Delete button */}
                          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDeleteFont('body', 'primary');
                              }}
                              className="w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors hover:scale-110"
                              title="Supprimer cette police"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                          
                          {/* Edit overlay */}
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-lg flex items-center justify-center transition-all">
                            <div className="opacity-0 group-hover:opacity-100 bg-white rounded-full p-2 shadow-md transition-opacity">
                              <Edit3 className="w-4 h-4 text-gray-600" />
                            </div>
                          </div>
                          
                          <div 
                            className="text-2xl font-normal mb-3 relative z-0"
                            style={{ fontFamily: `"${brand.visualIdentity.typography.body.primary}", sans-serif` }}
                          >
                            {brand.visualIdentity.typography.body.primary}
                          </div>
                          <div className="flex items-center gap-2 relative z-0">
                            {(() => {
                              const fontInfo = getGoogleFontInfo(brand.visualIdentity.typography.body.primary);
                              return fontInfo.isGoogleFont ? (
                                <>
                                  <div className="flex items-center gap-1">
                                    <img 
                                      src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" 
                                      alt="Google" 
                                      className="w-4 h-4" 
                                    />
                                    <span className="text-xs text-gray-500">Google</span>
                                  </div>
                                  <span className="text-xs text-gray-500">{fontInfo.styles} styles</span>
                                  <span className="text-xs text-gray-500">Free</span>
                                </>
                              ) : (
                                <span className="text-xs text-gray-500">System Font</span>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* No fonts fallback */}
                  {(!brand.visualIdentity?.typography?.headings?.h1) && (!brand.visualIdentity?.typography?.body?.primary) && (
                    <div className="text-center py-8 text-gray-500">
                      <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm">{t('brands.edit.brandStyles.fonts.noFonts', 'Aucune police définie')}</p>
                    </div>
                  )}
                  
                  {/* Additional Typography Details */}
                  {(brand.visualIdentity?.typography?.headings?.h2 || brand.visualIdentity?.typography?.headings?.h3 || 
                    brand.visualIdentity?.typography?.body?.secondary || brand.visualIdentity?.typography?.specialty?.buttons) && (
                    <div className="space-y-4">
                      <div className="text-sm font-medium text-gray-700">Additional Typography</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {brand.visualIdentity?.typography?.headings?.h2 && (
                          <div className="text-sm p-3 bg-gray-50 rounded-lg">
                            <span className="font-medium text-gray-600">H2:</span> {brand.visualIdentity.typography.headings.h2}
                          </div>
                        )}
                        {brand.visualIdentity?.typography?.headings?.h3 && (
                          <div className="text-sm p-3 bg-gray-50 rounded-lg">
                            <span className="font-medium text-gray-600">H3:</span> {brand.visualIdentity.typography.headings.h3}
                          </div>
                        )}
                        {brand.visualIdentity?.typography?.body?.secondary && (
                          <div className="text-sm p-3 bg-gray-50 rounded-lg">
                            <span className="font-medium text-gray-600">Secondary:</span> {brand.visualIdentity.typography.body.secondary}
                          </div>
                        )}
                        {brand.visualIdentity?.typography?.specialty?.buttons && (
                          <div className="text-sm p-3 bg-gray-50 rounded-lg">
                            <span className="font-medium text-gray-600">Buttons:</span> {brand.visualIdentity.typography.specialty.buttons}
                          </div>
                        )}
                        {brand.visualIdentity?.typography?.specialty?.captions && (
                          <div className="text-sm p-3 bg-gray-50 rounded-lg">
                            <span className="font-medium text-gray-600">Captions:</span> {brand.visualIdentity.typography.specialty.captions}
                          </div>
                        )}
                        {brand.visualIdentity?.typography?.specialty?.navigation && (
                          <div className="text-sm p-3 bg-gray-50 rounded-lg">
                            <span className="font-medium text-gray-600">Navigation:</span> {brand.visualIdentity.typography.specialty.navigation}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Typography Recommendations */}
                  {brand.visualIdentity?.typography?.recommendations?.improvements && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-purple-500" />
                        {t('brands.edit.brandStyles.fonts.recommendations', 'Typography Recommendations')}
                      </label>
                      <div className="text-xs text-gray-600 bg-purple-50 p-3 rounded-lg">
                        {brand.visualIdentity.typography.recommendations.improvements}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              </div>

              {/* Bottom row: Visual Strategy (full width) */}
              <div className="bg-white rounded-xl border border-black shadow-lg p-6">
                <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
                  <Target className="w-5 h-5 text-green-600" />
                  {t('brands.edit.brandStyles.visual.title', 'Visual Strategy')}
                </h2>
                <div className="space-y-4">
                  {/* Color Usage Guidelines */}
                  {brand.visualContentStrategy?.colorUsage && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('brands.edit.brandStyles.visual.colorUsage', 'Color Usage Guidelines')}
                      </label>
                      <div className="space-y-2">
                        {brand.visualContentStrategy.colorUsage.socialMediaPosts && (
                          <div className="text-xs text-gray-600">
                            <span className="font-medium">Social Media:</span> {brand.visualContentStrategy.colorUsage.socialMediaPosts}
                          </div>
                        )}
                        {brand.visualContentStrategy.colorUsage.callToActions && (
                          <div className="text-xs text-gray-600">
                            <span className="font-medium">CTAs:</span> {brand.visualContentStrategy.colorUsage.callToActions}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Typography Guidelines */}
                  {brand.visualContentStrategy?.typographyGuidelines && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('brands.edit.brandStyles.visual.typography', 'Typography Guidelines')}
                      </label>
                      <div className="space-y-2">
                        {brand.visualContentStrategy.typographyGuidelines.socialMediaHeaders && (
                          <div className="text-xs text-gray-600">
                            <span className="font-medium">Headers:</span> {brand.visualContentStrategy.typographyGuidelines.socialMediaHeaders}
                          </div>
                        )}
                        {brand.visualContentStrategy.typographyGuidelines.bodyText && (
                          <div className="text-xs text-gray-600">
                            <span className="font-medium">Body:</span> {brand.visualContentStrategy.typographyGuidelines.bodyText}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Visual Elements to Emphasize */}
                  {brand.visualContentStrategy?.visualElementsToEmphasize && brand.visualContentStrategy.visualElementsToEmphasize.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('brands.edit.brandStyles.visual.elements', 'Visual Elements to Emphasize')}
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {brand.visualContentStrategy.visualElementsToEmphasize.map((element: string, index: number) => (
                          <span
                            key={`visualElement-${element}-${index}`}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-50 text-green-700"
                          >
                            {element}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Design Consistency Tips */}
                  {brand.visualContentStrategy?.designConsistencyTips && brand.visualContentStrategy.designConsistencyTips.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('brands.edit.brandStyles.visual.consistency', 'Design Consistency Tips')}
                      </label>
                      <ul className="space-y-1">
                        {brand.visualContentStrategy.designConsistencyTips.map((tip: string, index: number) => (
                          <li key={`designTip-${tip}-${index}`} className="flex items-start gap-2">
                            <div className="w-1 h-1 bg-gray-400 rounded-full mt-2 flex-shrink-0" />
                            <span className="text-xs text-gray-600">{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : ( // Summary Tab
            <div className="grid grid-cols-1 gap-6">
              {/* Website Analysis Overview */}
              <div className="bg-white rounded-xl border border-black shadow-lg p-6">
                 <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary-600" />
                  {t('brands.edit.recap', 'Overview')}
                </h2>
                {brand.summary?.recap ? (
                  <div className="space-y-4">
                     <p className="text-sm text-gray-500">
                      {t('brands.edit.recapDescription', 'AI-generated summary of your website')}
                    </p>
                    <textarea
                      value={brand.summary.recap}
                      onChange={(e) => updateBrand('summary', { ...brand.summary, recap: e.target.value })}
                      rows={8}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    />
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>{t('brands.edit.summary.noData', 'No website analysis data available. Try analyzing your website to get insights.')}</p>
                  </div>
                )}
              </div>

              {/* Key Insights */}
               <div className="bg-white rounded-xl border border-black shadow-lg p-6 space-y-3">
                <h2 className="text-xl font-semibold mb-2 flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-yellow-500" />
                  {t('brands.edit.keyInsights', 'Key Insights')}
                </h2>
                {brand.summary?.takeaways && brand.summary.takeaways.length > 0 ? (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-500 mb-4"> {/* Added margin bottom */}
                      {t('brands.edit.keyInsightsDescription', 'Important observations about your website')}
                    </p>
                    {/* Use AddItemInput and list rendering for takeaways */}
                     <AddItemInput
                      placeholder={t('brands.edit.summary.takeaways', 'Add a key takeaway')}
                      onAdd={(value) => updateBrand('summary', { ...brand.summary, takeaways: [...brand.summary.takeaways, value] })}
                      useLargeButton={true}
                      existingItems={brand.summary.takeaways}
                    />
                     {Array.isArray(brand.summary.takeaways) && brand.summary.takeaways.length > 0 && (
                        <ul className="space-y-2 pt-2">
                          {brand.summary.takeaways.map((item: string, index: number) => (
                            <li key={`takeaway-${item}-${index}`} className="flex items-start gap-2 group">
                              <Lightbulb className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                              <span className="flex-1 text-sm text-gray-700">{item}</span>
                              <button
                                type="button"
                                onClick={() => updateBrand('summary', { ...brand.summary, takeaways: brand.summary.takeaways.filter((_: string, i: number) => i !== index) })}
                                className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600 transition-opacity"
                                aria-label={`${t('common.delete', 'Delete')} ${item}`}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                  </div>
                ) : (
                 <> {/* Show input even if no data */}
                    <p className="text-sm text-gray-500 mb-4"> 
                      {t('brands.edit.keyInsightsDescription', 'Important observations about your website')}
                    </p>
                    <AddItemInput
                      placeholder={t('brands.edit.summary.takeaways', 'Add a key takeaway')}
                      onAdd={(value) => updateBrand('summary', { ...brand.summary, takeaways: [value] })} // Start new array
                      useLargeButton={true}
                    />
                     <div className="text-center py-4 text-gray-500">
                        <p>{t('brands.edit.summary.noDataYet', 'No website analysis data available yet. Add takeaways manually or analyze your website.')}</p> {/* Adjusted message */}
                      </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <AddSocialNetworkModal
        isOpen={isAddNetworkModalOpen}
        onClose={() => setIsAddNetworkModalOpen(false)}
        onAdd={handleAddSocialNetwork}
        existingPlatforms={(brand.socialMedias || []).map((sm: SocialMedia) => sm.platform)}
      />

      {/* Color Edit Modal */}
      {isColorModalOpen && (
        <ColorEditModal
          isOpen={isColorModalOpen}
          onClose={() => {
            setIsColorModalOpen(false);
            setEditingColor(null);
          }}
          onSave={handleSaveColor}
          initialColor={editingColor?.color || '#000000'}
          initialType={editingColor?.type || 'primary'}
          isEditing={editingColor !== null}
        />
      )}

      {/* Font Edit Modal */}
      {fontModal && (
        <FontEditModal
          isOpen={fontModal}
          onClose={() => {
            setFontModal(false);
            setEditingFont(null);
          }}
          onSave={handleSaveFont}
          initialFont={editingFont?.font || ''}
          fontType={editingFont?.type || 'heading'}
          fontSubtype={editingFont?.subtype}
        />
      )}

      {/* Refresh Data Warning Modal */}
      {isRefreshModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 border border-black shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {t('brands.edit.refresh.modal.title', 'Refresh Brand Data')}
              </h3>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-3">
                {t('brands.edit.refresh.modal.description', 'This will re-analyze your website and update all brand data including:')}
              </p>
              <ul className="text-sm text-gray-600 space-y-1 ml-4">
                <li>• {t('brands.edit.refresh.modal.items.colors', 'Colors and visual identity')}</li>
                <li>• {t('brands.edit.refresh.modal.items.fonts', 'Typography and fonts')}</li>
                <li>• {t('brands.edit.refresh.modal.items.content', 'Content strategy')}</li>
                <li>• {t('brands.edit.refresh.modal.items.description', 'Brand description and values')}</li>
              </ul>
              <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-sm text-orange-800 font-medium">
                  ⚠️ {t('brands.edit.refresh.modal.warning', 'Warning: All current data will be overwritten and cannot be undone.')}
                </p>
              </div>
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setIsRefreshModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleRefreshData}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors"
              >
                {t('brands.edit.refresh.modal.confirm', 'Yes, Refresh Data')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Refresh Success Modal */}
      {isRefreshSuccessModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 border border-black shadow-lg text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <RefreshCw className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {t('brands.edit.refresh.modal.successTitle', 'Data Refreshed!')}
            </h3>
            <p className="text-gray-600 mb-6">
              {t('brands.edit.refresh.success', 'Brand data refreshed successfully!')}
            </p>
            <button
              onClick={() => setIsRefreshSuccessModalOpen(false)}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
            >
              {t('common.ok', 'OK')}
            </button>
          </div>
        </div>
      )}

      {/* Fill with AI Warning Modal */}
      {isFillWithAIModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 border border-black shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {t('brands.edit.fillWithAI.modal.title', 'Fill Brand with AI')}
              </h3>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-3">
                {t('brands.edit.fillWithAI.modal.description', 'This will analyze a website and populate all brand data including:')}
              </p>
              <ul className="text-sm text-gray-600 space-y-1 ml-4">
                <li>• {t('brands.edit.fillWithAI.modal.items.colors', 'Colors and visual identity')}</li>
                <li>• {t('brands.edit.fillWithAI.modal.items.fonts', 'Typography and fonts')}</li>
                <li>• {t('brands.edit.fillWithAI.modal.items.content', 'Content strategy')}</li>
                <li>• {t('brands.edit.fillWithAI.modal.items.description', 'Brand description and values')}</li>
              </ul>
              <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <p className="text-sm text-purple-800 font-medium">
                  ⚠️ {t('brands.edit.fillWithAI.modal.warning', 'Warning: All current data will be overwritten and cannot be undone.')}
                </p>
              </div>
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setIsFillWithAIModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={() => {
                  setIsFillWithAIModalOpen(false);
                  setIsUrlInputModalOpen(true);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
              >
                {t('brands.edit.fillWithAI.modal.confirm', 'Continue')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* URL Input Modal */}
      {isUrlInputModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 border border-black shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Globe className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {t('brands.edit.fillWithAI.urlModal.title', 'Enter Website URL')}
              </h3>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-3">
                {t('brands.edit.fillWithAI.urlModal.description', 'Please enter the website URL you want to analyze:')}
              </p>
              <div className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-gray-400" />
                <input
                  type="url"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder={t('brands.edit.fillWithAI.urlModal.placeholder', 'https://example.com')}
                  className="flex-1 rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  autoFocus
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setIsUrlInputModalOpen(false);
                  setUrlInput('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={() => {
                  if (urlInput.trim()) {
                    handleFillWithAI(urlInput.trim());
                    setUrlInput('');
                  }
                }}
                disabled={!urlInput.trim()}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  urlInput.trim()
                    ? 'text-white bg-purple-600 hover:bg-purple-700'
                    : 'text-gray-400 bg-gray-200 cursor-not-allowed'
                }`}
              >
                {t('brands.edit.fillWithAI.urlModal.analyze', 'Analyze Website')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Brand Generation Loader for Refresh */}
      <BrandGenerationLoader isVisible={isRefreshing} />
    </div>
  );
};

// Wrap BrandForm with ErrorBoundary to catch DOM manipulation errors
const BrandFormWithErrorBoundary = () => (
  <BrandFormErrorBoundary>
    <BrandForm />
  </BrandFormErrorBoundary>
);

export default BrandFormWithErrorBoundary;